import React, { useEffect, useRef, useState } from 'react';

import { ArrowLeftOutlined } from '@ant-design/icons';
import { changePasswordByEmailStart } from '@api/FW/changePassword/changePasswordByEmail';
import { notificationService } from '@app/services/notification.service';
import { Alert, Button, Collapse, Flex, Form, Input, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthWrap } from './AuthWrap.styled';

type LoginFormData = {
	username: string;
	email: string;
};

const PasswordRecoveryForm = () => {
	const { t } = useTranslation();
	const { Text } = Typography;

	const navigate = useNavigate();
	const url = window.location.origin + '/';
	const key: string = process.env.REACT_APP_RECAPTCHA_KEY || '';
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	const [disableButton, setDisableButton] = useState(false);
	const [captchValue, setCaptchValue] = useState<string | null>();
	const [inputLogin, setInputLogin] = useState<string | undefined>();
	const [inputEmail, setInputEmail] = useState<string | undefined>();
	const [loading, setLoading] = useState(false);
	const [requestSuccessful, setRequestSuccessful] = useState(false);

	useEffect(() => {
		if (captchValue && inputLogin && inputEmail) {
			setDisableButton(false);
		} else {
			setDisableButton(true);
		}
	}, [captchValue, inputLogin, inputEmail]);

	const submitForm = (values: LoginFormData) => {
		setLoading(true);

		const requestData = {
			baseUrl: url,
			email: values.email,
			login: values.username,
			recaptcha: captchValue,
		};

		changePasswordByEmailStart(requestData)
			.then(() => {
				setRequestSuccessful(true);
			})
			.catch((err) => {
				notificationService.error({
					message: err.message ? err.message : t('api.error'),
				});

				recaptchaRef.current?.reset();
			})
			.finally(() => setLoading(false));
	};

	const onChange = (value: string | null) => {
		setCaptchValue(value);
	};

	return (
		<AuthWrap>
			{requestSuccessful ? (
				<>
					<Title level={3}>{inputLogin}</Title>
					<Typography.Title level={4} style={{ marginBottom: 10, padding: '0 10%' }}>
						{t('passwordRecovery.passwordRecoverySuccess')}
					</Typography.Title>
				</>
			) : (
				<>
					<Button onClick={() => navigate('/auth/login')}>
						<ArrowLeftOutlined /> {t('passwordRecovery.passwordRecoveryReturn')}
					</Button>
					<Flex vertical>
						<Title level={3}>{t('passwordRecovery.passwordRecoveryTitle')}</Title>
						<Text type='secondary'>{t('passwordRecovery.passwordRecoveryText')}</Text>
					</Flex>

					<Collapse
						items={[
							{
								key: '1',
								label: t('passwordRecovery.alertText').split('\n')[0],
								styles: { header: { backgroundColor: '#f8c8c8' } },
								children: (
									<Alert
										style={{ whiteSpace: 'pre-line' }}
										message={t('passwordRecovery.alertText')}
										type='error'
									/>
								),
							},
						]}
					/>
					<Form layout='vertical' onFinish={submitForm} size='large'>
						<Flex gap={16} vertical>
							<Form.Item<LoginFormData>
								name='username'
								label={t('auth.usernamePlaceholder')}
								rules={[{ required: true, message: t('auth.usernameError') }]}>
								<Input
									placeholder={t('auth.usernamePlaceholder')}
									value={inputLogin}
									onChange={(e) => setInputLogin(e.target.value)}
								/>
							</Form.Item>

							<Form.Item<LoginFormData>
								name='email'
								label={t('Email')}
								rules={[{ type: 'email', required: true }]}>
								<Input
									placeholder={t('Email')}
									value={inputEmail}
									onChange={(e) => setInputEmail(e.target.value)}
								/>
							</Form.Item>
							<ReCAPTCHA
								sitekey={key}
								ref={recaptchaRef}
								onChange={onChange}
								style={{
									marginBottom: 20,
								}}
							/>

							<Form.Item>
								<Button type='primary' htmlType='submit' loading={loading} disabled={disableButton}>
									{t('passwordRecovery.passwordRecoverySend')}
								</Button>
							</Form.Item>
						</Flex>
					</Form>
				</>
			)}
		</AuthWrap>
	);
};

export default PasswordRecoveryForm;
